// One sharp book, devigged — not the average of everyone.
//
// Averaging the board gives every shop an equal vote, so twelve copies of the
// same soft line outvote the one price that actually moves on money. Pinnacle
// and the Betfair exchange are where the money is, so the sharpest book present
// is taken as the whole opinion and the rest of the board is ignored.
//
// The exchange needs one correction first. A back price of 2.00 on Betfair does
// not pay 2.00: commission comes off the win, so the punter's real return is
// 1 + 1.00 x (1 - 0.05) = 1.95. Devigging the quoted price would read the
// exchange as more generous than it is and hand every edge calculation a
// systematic 5% of the profit that never existed.

import { bookFair } from '../universe.js';
import { COMMISSION, EXCHANGES, netPrice } from '../exchange.js';
import { assertBefore } from './base.js';

/**
 * The exchange list and the commission figure, re-exported under the names the
 * app has always imported them by. They LIVE in `engine/exchange.js` now — a
 * leaf with no imports — because `universe.js` had to be able to read them to
 * make the same correction inside `consensus`, and it cannot import this module
 * without a cycle (engine review #14).
 */
export { COMMISSION, EXCHANGES };

const DEFAULTS = { books: ['pinnacle', 'betfair_ex_au'], devig: 'power', commission: COMMISSION };

/** Is this book quoting anything at all on this match? */
function prices(match, bookmaker) {
  return Object.values(match?.prices ?? {}).some((rows) => rows.some((r) => r.bookmaker === bookmaker));
}

/** A copy of the match with one book's prices net of commission. */
function deflate(match, bookmaker, commission) {
  const out = {};
  for (const [selection, rows] of Object.entries(match.prices ?? {})) {
    out[selection] = rows.map((r) => (r.bookmaker === bookmaker
      ? { ...r, price: netPrice(r.price, r.bookmaker, commission) }
      : r));
  }
  return { ...match, prices: out };
}

/**
 * `bookFair` for one book taken as THE WHOLE MARKET, with the commission
 * correction applied where the book charges one. Null if the book is not
 * pricing the match.
 *
 * It still deflates here, and that is not a duplicate of `consensus`'s own
 * correction (engine review #14): `bookFair` reads a QUOTE, `consensus` reads
 * OPINIONS, and this function is the bridge — one book read as an opinion. The
 * two now agree on what the exchange's opinion is, which is the whole of the
 * fix; what they must not do is both make the correction on one call.
 *
 * Exported because `runStrategy` needs the same reading when Settings names a
 * single book as the market, and the racing view's exchange column is it.
 *
 * `method` unnamed is the MARKET's own default — `defaultDevig` in universe.js,
 * which is Shin on a race and power on a fixture — and not this model's, which
 * is power everywhere and stays that way (`DEFAULTS.devig`, read by `predict`
 * below). One book read as an opinion has to be read the way the consensus
 * reads its opinions, or a race would have two fair prices again depending on
 * whether the exchange was the whole market or one vote in it.
 */
export function bookFairNet(match, selection, bookmaker, method = null, commission = COMMISSION) {
  if (!prices(match, bookmaker)) return null;
  const board = EXCHANGES.includes(bookmaker) ? deflate(match, bookmaker, commission) : match;
  return bookFair(board, selection, bookmaker, method);
}

export const sharpAnchor = {
  id: 'sharp-anchor',
  version: 1,
  sports: null,
  needsFit: false,
  defaultParams: { ...DEFAULTS },

  fit(history, asOf, params = {}, { strict = false } = {}) {
    assertBefore(history, asOf, strict);
    return { ...DEFAULTS, ...params, fittedTo: asOf };
  },

  predict(state, match, selection) {
    const books = state?.books ?? DEFAULTS.books;
    const method = state?.devig ?? DEFAULTS.devig;
    const commission = state?.commission ?? DEFAULTS.commission;
    for (const book of books) {
      const p = bookFairNet(match, selection, book, method, commission);
      if (p !== null) return p;
    }
    return null;
  },
};
